import React, { useState, useEffect } from 'react';
import { X, Cookie, Settings } from 'lucide-react';
import { Button } from './ui/button';

const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, can't be disabled
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // Check if user has already made a choice
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (!cookieConsent) {
      // Small delay for better UX
      const timer = setTimeout(() => setIsVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const consent = {
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consent));
    
    // Track consent
    trackConsentEvent('accept_all');
    
    setIsVisible(false);
  };

  const handleSavePreferences = () => {
    const consent = {
      ...preferences,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consent));
    
    // Track consent
    trackConsentEvent('custom_preferences', preferences);
    
    setIsVisible(false);
    setShowPreferences(false);
  };

  const handleRejectNonEssential = () => {
    const consent = {
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consent));
    
    // Track consent
    trackConsentEvent('reject_non_essential');
    
    setIsVisible(false);
  };

  const trackConsentEvent = (action, data = {}) => {
    // Analytics tracking for consent
    console.log('[Analytics] Cookie Consent:', { action, ...data, timestamp: new Date().toISOString() });
    
    // Store for analytics
    const events = JSON.parse(sessionStorage.getItem('consentEvents') || '[]');
    events.push({ event: 'cookie_consent', action, data, timestamp: new Date().toISOString() });
    sessionStorage.setItem('consentEvents', JSON.stringify(events));
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[100] p-4 bg-gray-900/95 backdrop-blur-lg border-t border-gray-700 shadow-2xl">
      <div className="max-w-4xl mx-auto">
        {!showPreferences ? (
          // Main Banner
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex items-start gap-3 flex-1">
              <Cookie className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-white text-sm font-medium mb-1">
                  We use cookies to improve your experience
                </p>
                <p className="text-gray-400 text-xs">
                  We use cookies for essential functionality and to analyze how you use our service. 
                  <button 
                    onClick={() => setShowPreferences(true)}
                    className="text-yellow-500 hover:text-yellow-400 ml-1 underline"
                  >
                    Learn more
                  </button>
                </p>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-2 w-full sm:w-auto">
              <Button
                onClick={handleAcceptAll}
                className="flex-1 sm:flex-none bg-yellow-500 hover:bg-yellow-600 text-black font-medium px-4 py-2 text-sm"
              >
                Accept All
              </Button>
              <Button
                onClick={() => setShowPreferences(true)}
                variant="outline"
                className="flex-1 sm:flex-none border-gray-600 text-gray-300 hover:bg-gray-800 px-4 py-2 text-sm"
              >
                <Settings className="w-4 h-4 mr-1" />
                Manage
              </Button>
            </div>
          </div>
        ) : (
          // Preferences Panel
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-white font-semibold flex items-center gap-2">
                <Settings className="w-5 h-5 text-yellow-500" />
                Cookie Preferences
              </h3>
              <button
                onClick={() => setShowPreferences(false)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-3">
              {/* Necessary */}
              <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                <div>
                  <p className="text-white text-sm font-medium">Necessary</p>
                  <p className="text-gray-400 text-xs">Required for the app to function</p>
                </div>
                <div className="bg-emerald-500/20 text-emerald-400 text-xs px-2 py-1 rounded">
                  Always on
                </div>
              </div>
              
              {/* Analytics */}
              <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                <div>
                  <p className="text-white text-sm font-medium">Analytics</p>
                  <p className="text-gray-400 text-xs">Help us improve by tracking usage</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={preferences.analytics}
                    onChange={(e) => setPreferences(p => ({ ...p, analytics: e.target.checked }))}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-500"></div>
                </label>
              </div>
              
              {/* Marketing */}
              <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                <div>
                  <p className="text-white text-sm font-medium">Marketing</p>
                  <p className="text-gray-400 text-xs">Personalized content and offers</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={preferences.marketing}
                    onChange={(e) => setPreferences(p => ({ ...p, marketing: e.target.checked }))}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-500"></div>
                </label>
              </div>
            </div>
            
            <div className="flex gap-2 pt-2">
              <Button
                onClick={handleRejectNonEssential}
                variant="outline"
                className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-800 text-sm"
              >
                Reject Non-Essential
              </Button>
              <Button
                onClick={handleSavePreferences}
                className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-black font-medium text-sm"
              >
                Save Preferences
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieBanner;
